import { FieldTemplate, FormStatus } from "api/models";
import { FormikValues } from "formik";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { onSaveProps } from "../FormLayout";
import { FieldType } from "api/models/FieldTemplate";

export function useAutoSaveFormLayout(
  formik: FormikValues,
  templates: FieldTemplate[],
  mode: "create" | "edit" | "view",
  onSave: ({ values, showToast, status }: onSaveProps) => Promise<string | undefined>,
  currentStatus: FormStatus | undefined
) {
  const prevValuesRef = useRef<FormikValues>({});
  const lastSerializedValuesRef = useRef<string>("");

  const fieldTypeMap = useMemo(() => {
    const map: Record<string, FieldType> = {};

    for (const t of templates) {
      const fieldId = removeRepeatableIndex(t.id);
      map[fieldId] = t.fieldType;
    }

    return map;
  }, [templates]);

  const handleChanges = useCallback(
    (currentValues: FormikValues) => {
      const prevValues = prevValuesRef.current;

      const changedIds: string[] = [];
      for (const key in currentValues) {
        if (currentValues[key] !== prevValues[key]) {
          const changedId = removeRepeatableIndex(key);
          changedIds.push(changedId);
        }
      }

      if (changedIds.length === 0) {
        return;
      }

      const hasChangedNonCalculatedFields = changedIds.some(
        (id) => fieldTypeMap[id] !== FieldType.Calculated
      );

      if (hasChangedNonCalculatedFields) {
        onSave({
          values: currentValues,
          status:
            currentStatus === FormStatus.New
              ? FormStatus.New
              : FormStatus.Filling,
          showToast: false,
        });
      }

      prevValuesRef.current = { ...currentValues };
    },
    [fieldTypeMap, onSave, currentStatus]
  );

  const debouncedHandleChanges = useDebouncedCallback(handleChanges, 1000);

  useEffect(() => {
    if (mode === "view") {
      return;
    }

    // Shallow detect value changes using a simple lightweight string hash
    const serialized = JSON.stringify(formik.values);
    if (serialized !== lastSerializedValuesRef.current) {
      lastSerializedValuesRef.current = serialized;
      debouncedHandleChanges(formik.values);
    }
  }, [formik.values, mode, debouncedHandleChanges]);

  function removeRepeatableIndex(id: string) {
    return id.split(':')[0];
  }
}
